import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, MessageCircleHeart, Moon, Sparkles, Sunrise } from "lucide-react";
import { cn } from "../../../lib/utils";
import type { RitualKey } from "../../../api/lifeOs";
import { useRitualDay } from "../../../hooks/useRitualDay";
import { rhythmChips } from "../../../lib/rituals";
import { RitualSession } from "../cockpit/Rituals";

const ICON: Record<RitualKey, React.ElementType> = {
  morning: Sunrise,
  checkin: MessageCircleHeart,
  evening: Moon,
  weekly: Sparkles,
};

/** Morning · Check in · Evening — the rituals as the rhythm of the day (spec 2026-11-04 §3).
 *  One soft pill like the mode switch beside it; the ritual that's due lifts out as a raised chip. */
const RhythmLine = ({ now = () => new Date() }: { now?: () => Date }) => {
  const { t } = useTranslation();
  const { hasCompass, prefs, log } = useRitualDay();
  const [open, setOpen] = useState<RitualKey | null>(null);

  if (!hasCompass && !open) return null;
  const chips = rhythmChips(prefs, log, now());

  return (
    <>
      <div
        role="group"
        aria-label={t("todayHome.rhythm.label")}
        className="inline-flex max-w-full flex-nowrap items-center gap-0.5 rounded-full border border-gray-200 bg-white/70 p-1"
      >
        {chips.map((chip) => {
          const Icon = ICON[chip.ritual] ?? Sparkles;
          const due = chip.state === "due";
          const done = chip.state === "done";
          return (
            <button
              key={chip.slot}
              type="button"
              aria-pressed={chip.slot === "checkin" ? undefined : done}
              onClick={() => setOpen(chip.ritual)}
              className={cn(
                "relative inline-flex min-h-[44px] shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 text-[13px] sm:px-3 sm:text-sm transition-colors motion-reduce:transition-none",
                due
                  ? "bg-card font-medium text-primary shadow-sm ring-1 ring-primary/15"
                  : chip.slot === "checkin"
                    ? "text-foreground hover:bg-muted/70"
                    : "text-muted-foreground hover:bg-muted/70 hover:text-foreground"
              )}
            >
              {done ? (
                <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-primary/15">
                  <Check className="h-3 w-3 text-primary" aria-hidden />
                </span>
              ) : (
                <Icon className={cn("h-4 w-4", due ? "text-primary" : "opacity-70")} aria-hidden />
              )}
              {t(`todayHome.rhythm.${chip.ritual}`)}
              {due && (
                <span className="inline-flex items-center gap-1 text-xs font-normal text-primary/80">
                  <span aria-hidden className="relative inline-flex h-1.5 w-1.5">
                    <span className="absolute inset-0 rounded-full bg-primary/50 motion-safe:animate-ping" />
                    <span className="relative h-1.5 w-1.5 rounded-full bg-primary" />
                  </span>
                  {/* Phones show just the dot; the word stays for screen readers. */}
                  <span className="sr-only sm:not-sr-only">{t("todayHome.rhythm.now")}</span>
                </span>
              )}
            </button>
          );
        })}
      </div>
      {open && <RitualSession ritualKey={open} onClose={() => setOpen(null)} />}
    </>
  );
};

export default RhythmLine;
